import React, { useState, useEffect } from 'react';
import { 
    Card, 
    Button, 
    Switch, 
    Typography, 
    Space, 
    Alert, 
    List, 
    Tag, 
    Divider,
    Modal,
    Progress,
    notification,
    Row,
    Col
} from 'antd';
import { 
    DownloadOutlined, 
    WifiOutlined, 
    CloudSyncOutlined,
    MobileOutlined,
    BellOutlined,
    SettingOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;

const PWAFeatures = () => {
    const [pwaState, setPwaState] = useState({
        isInstalled: false,
        isOnline: navigator.onLine,
        canInstall: false,
        installPrompt: null,
        backgroundSync: false,
        pushNotifications: false,
        offlineMode: false,
        cacheSize: 0,
        lastSync: null
    });
    const [installProgress, setInstallProgress] = useState(0);
    const [showInstallModal, setShowInstallModal] = useState(false);

    useEffect(() => {
        initializePWA();
        setupEventListeners();
        checkInstallability();
    }, []);

    const initializePWA = () => {
        // Check if app is already installed
        const isInstalled = window.matchMedia('(display-mode: standalone)').matches ||
                           window.navigator.standalone === true;
        
        setPwaState(prev => ({
            ...prev,
            isInstalled,
            isOnline: navigator.onLine
        }));

        // Check service worker registration
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.ready.then(registration => {
                console.log('Service Worker is ready');
            });
        }
    };

    const setupEventListeners = () => {
        // Online/Offline status
        window.addEventListener('online', () => {
            setPwaState(prev => ({ ...prev, isOnline: true }));
            notification.success({
                message: 'Back Online',
                description: 'You are now connected to the internet'
            });
        });

        window.addEventListener('offline', () => {
            setPwaState(prev => ({ ...prev, isOnline: false }));
            notification.warning({
                message: 'Offline Mode',
                description: 'You are now offline. Some features may be limited.'
            });
        });

        // Before install prompt
        window.addEventListener('beforeinstallprompt', (e) => {
            e.preventDefault();
            setPwaState(prev => ({
                ...prev,
                canInstall: true,
                installPrompt: e
            }));
        });

        // App installed
        window.addEventListener('appinstalled', () => {
            setPwaState(prev => ({ ...prev, isInstalled: true, canInstall: false }));
            notification.success({
                message: 'App Installed',
                description: 'Spoon & Spice has been installed successfully!'
            });
        });
    };

    const checkInstallability = () => {
        // Check if app can be installed
        const isInstallable = !pwaState.isInstalled && 
                             ('serviceWorker' in navigator) && 
                             ('PushManager' in window);
        
        setPwaState(prev => ({ ...prev, canInstall: isInstallable }));
    };

    const handleInstall = async () => {
        if (pwaState.installPrompt) {
            setShowInstallModal(true);
            setInstallProgress(0);
            
            // Simulate installation progress
            const progressInterval = setInterval(() => {
                setInstallProgress(prev => {
                    if (prev >= 100) {
                        clearInterval(progressInterval);
                        return 100;
                    }
                    return prev + 10;
                });
            }, 200);

            try {
                const result = await pwaState.installPrompt.prompt();
                console.log('Install prompt result:', result);
                
                if (result.outcome === 'accepted') {
                    setPwaState(prev => ({ ...prev, isInstalled: true, canInstall: false }));
                }
            } catch (error) {
                console.error('Installation failed:', error);
                notification.error({
                    message: 'Installation Failed',
                    description: 'Failed to install the app. Please try again.'
                });
            }
        }
    };

    const handleBackgroundSync = async (enabled) => {
        if (enabled && 'serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype) {
            try {
                const registration = await navigator.serviceWorker.ready;
                await registration.sync.register('background-sync');
                setPwaState(prev => ({ ...prev, backgroundSync: true }));
                notification.success({
                    message: 'Background Sync Enabled',
                    description: 'Your data will sync automatically in the background'
                });
            } catch (error) {
                console.error('Background sync setup failed:', error);
                notification.error({
                    message: 'Background Sync Failed',
                    description: 'Failed to enable background sync'
                });
            }
        } else {
            setPwaState(prev => ({ ...prev, backgroundSync: false }));
        }
    };

    const handlePushNotifications = async (enabled) => {
        if (enabled) {
            try {
                const permission = await Notification.requestPermission();
                if (permission === 'granted') {
                    setPwaState(prev => ({ ...prev, pushNotifications: true }));
                    notification.success({
                        message: 'Push Notifications Enabled',
                        description: 'You will receive notifications even when the app is closed'
                    });
                } else {
                    notification.warning({
                        message: 'Permission Denied',
                        description: 'Push notifications require permission to be enabled'
                    });
                }
            } catch (error) {
                console.error('Push notification setup failed:', error);
            }
        } else {
            setPwaState(prev => ({ ...prev, pushNotifications: false }));
        }
    };

    const handleOfflineMode = (enabled) => {
        setPwaState(prev => ({ ...prev, offlineMode: enabled }));
        if (enabled) {
            notification.info({
                message: 'Offline Mode Enabled',
                description: 'You can use the app without internet connection'
            });
        }
    };

    const clearCache = async () => {
        try {
            if ('caches' in window) {
                const cacheNames = await caches.keys();
                await Promise.all(
                    cacheNames.map(cacheName => caches.delete(cacheName))
                );
                setPwaState(prev => ({ ...prev, cacheSize: 0 }));
                notification.success({
                    message: 'Cache Cleared',
                    description: 'All cached data has been cleared'
                });
            }
        } catch (error) {
            console.error('Cache clearing failed:', error);
        }
    };

    const getConnectionStatus = () => {
        if (pwaState.isOnline) {
            return <Tag color="green" icon={<WifiOutlined />}>Online</Tag>;
        } else {
            return <Tag color="orange" icon={<ExclamationCircleOutlined />}>Offline</Tag>;
        }
    };

    const getInstallStatus = () => {
        if (pwaState.isInstalled) {
            return <Tag color="green" icon={<CheckCircleOutlined />}>Installed</Tag>;
        } else if (pwaState.canInstall) {
            return <Tag color="blue" icon={<DownloadOutlined />}>Ready to Install</Tag>;
        } else {
            return <Tag color="default">Not Available</Tag>;
        }
    };

    return (
        <div className="pwa-features">
            <div className="mb-6">
                <Title level={2}>
                    <MobileOutlined className="mr-2" />
                    PWA Features
                </Title>
                <Text type="secondary">
                    Enhance your experience with Progressive Web App features
                </Text>
            </div>

            {/* Status Overview */}
            <Card title="App Status" className="mb-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center">
                        <div className="text-2xl mb-2">📱</div>
                        <Text strong>Installation</Text>
                        <div className="mt-1">{getInstallStatus()}</div>
                    </div>
                    <div className="text-center">
                        <div className="text-2xl mb-2">🌐</div>
                        <Text strong>Connection</Text>
                        <div className="mt-1">{getConnectionStatus()}</div>
                    </div>
                    <div className="text-center">
                        <div className="text-2xl mb-2">⚡</div>
                        <Text strong>Performance</Text>
                        <div className="mt-1">
                            <Tag color="green">Optimized</Tag>
                        </div>
                    </div>
                </div>
            </Card>

            {/* Installation */}
            {pwaState.canInstall && !pwaState.isInstalled && (
                <Card title="Install App" className="mb-4">
                    <Alert
                        message="Install Spoon & Spice"
                        description="Install this app on your device for a better experience. You'll get faster loading, offline access, and native app features."
                        type="info"
                        showIcon
                        action={
                            <Button type="primary" onClick={handleInstall}>
                                Install Now
                            </Button>
                        }
                    />
                </Card>
            )}

            {/* Features */}
            <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                    <Card title="App Features" size="small">
                        <List
                            dataSource={[
                                { 
                                    title: 'Background Sync', 
                                    description: 'Sync data automatically',
                                    enabled: pwaState.backgroundSync,
                                    onChange: handleBackgroundSync
                                },
                                { 
                                    title: 'Push Notifications', 
                                    description: 'Get notified even when app is closed',
                                    enabled: pwaState.pushNotifications,
                                    onChange: handlePushNotifications
                                },
                                { 
                                    title: 'Offline Mode', 
                                    description: 'Use app without internet',
                                    enabled: pwaState.offlineMode,
                                    onChange: handleOfflineMode
                                }
                            ]}
                            renderItem={(item) => (
                                <List.Item>
                                    <div className="flex justify-between items-center w-full">
                                        <div>
                                            <Text strong>{item.title}</Text>
                                            <div className="text-sm text-gray-500">
                                                {item.description}
                                            </div>
                                        </div>
                                        <Switch
                                            checked={item.enabled}
                                            onChange={item.onChange}
                                        />
                                    </div>
                                </List.Item>
                            )}
                        />
                    </Card>
                </Col>

                <Col xs={24} md={12}>
                    <Card title="Storage & Cache" size="small">
                        <div className="space-y-4">
                            <div className="flex justify-between items-center">
                                <Text>Cache Size</Text>
                                <Text>{pwaState.cacheSize} MB</Text>
                            </div>
                            
                            <div className="flex justify-between items-center">
                                <Text>Last Sync</Text>
                                <Text>
                                    {pwaState.lastSync ? 
                                        new Date(pwaState.lastSync).toLocaleString() : 
                                        'Never'
                                    }
                                </Text>
                            </div>
                            
                            <Divider />
                            
                            <Button 
                                onClick={clearCache}
                                className="w-full"
                            >
                                Clear Cache
                            </Button>
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Installation Modal */}
            <Modal
                title="Installing App"
                open={showInstallModal}
                closable={false}
                footer={null}
                width={400}
            >
                <div className="text-center space-y-4">
                    <div className="text-4xl">📱</div>
                    <Title level={4}>Installing Spoon & Spice</Title>
                    <Progress 
                        percent={installProgress} 
                        status={installProgress === 100 ? 'success' : 'active'}
                    />
                    <Text type="secondary">
                        {installProgress < 100 ? 
                            'Setting up your app...' : 
                            'Installation complete!'
                        }
                    </Text>
                </div>
            </Modal>
        </div>
    );
};

export default PWAFeatures;
